// ProfilePage — страница «Мой профиль» (R39b / U-10).
// RHF + Zod: редактируемые поля displayName/phone/position/description;
// username/email — readonly (смена через PATCH /api/users/me невозможна по дизайну backend).
// submit → usersApi.updateMe → updateCurrentUser (топ-бар без перелогина) + success Alert.
// Ограничение (известное, см. TODO-scratch): GET /api/auth/me не отдаёт phone/position/description,
// поэтому до первого сохранения эти поля пустые; после PATCH — запоминается ответ UserDto.
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Box, Stack, TextField, Button, Alert } from '@mui/material';
import { PageHeader } from '@/theme/PageHeader';
import { useAuth } from '@/auth/AuthContext';
import { usersApi, type UserDto, type UserProfileUpdateRequest } from '@/api/users';
import type { CurrentUserView } from '@/api/auth';
import { extractApiErrorMessage } from '@/lib/apiError';
import { t } from '@/i18n';

/** Zod-схема валидации профиля (лимиты — как у backend V29: phone ≤32, position ≤100). */
const profileSchema = z.object({
  displayName: z.string().min(1, 'Имя обязательно').max(100, 'Слишком длинное имя'),
  phone: z.string().max(32, 'Слишком длинный телефон').optional().or(z.literal('')),
  position: z.string().max(100, 'Слишком длинная должность').optional().or(z.literal('')),
  description: z.string().max(2000, 'Слишком длинное описание').optional().or(z.literal('')),
});

/** Тип значений формы профиля. */
export type ProfileFormValues = z.infer<typeof profileSchema>;

/** Преобразует UserDto (ответ PATCH /me) в CurrentUserView (AuthContext, R39b / U-10). */
function toCurrentUserView(dto: UserDto): CurrentUserView {
  return {
    id: dto.id,
    username: dto.username,
    email: dto.email,
    displayName: dto.displayName,
    status: dto.status,
    roles: dto.roles,
    // Backend DepartmentView может прийти с name/code = null — приводим к '' (CurrentUserView требует string).
    departments: dto.departments.map((d) => ({
      id: d.id,
      name: d.name ?? '',
      code: d.code ?? '',
      roleInDepartment: d.roleInDepartment,
    })),
    createdAt: dto.createdAt,
    updatedAt: dto.updatedAt,
  };
}

/** Страница «Мой профиль»: просмотр неизменяемых полей + редактирование профильных. */
export function ProfilePage(): JSX.Element {
  const { currentUser, updateCurrentUser } = useAuth();
  // Ответ последнего PATCH — источник истины для profile-полей + optimized lock.
  const [lastSaved, setLastSaved] = useState<UserDto | null>(null);

  const mutation = useMutation({
    mutationFn: (payload: UserProfileUpdateRequest) => usersApi.updateMe(payload),
    onSuccess: (dto) => {
      setLastSaved(dto);
      updateCurrentUser(toCurrentUserView(dto));
    },
  });

  const { control, handleSubmit, formState } = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      displayName: lastSaved?.displayName ?? currentUser?.displayName ?? '',
      phone: lastSaved?.phone ?? '',
      position: lastSaved?.position ?? '',
      description: lastSaved?.description ?? '',
    },
  });

  const onSubmit: SubmitHandler<ProfileFormValues> = (values) => {
    mutation.mutate({
      displayName: values.displayName,
      phone: values.phone || null,
      position: values.position || null,
      description: values.description || null,
      // Optimistic lock: на проводе — updatedAt из /me (или из последнего PATCH).
      expectedUpdatedAt: lastSaved?.updatedAt ?? currentUser?.updatedAt ?? null,
    });
  };

  return (
    <Box>
      <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} actions={null} />
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate sx={{ mt: 2 }}>
        <Stack spacing={2}>
          {mutation.isSuccess ? (
            <Alert severity="success">{t('profile.saveSuccess')}</Alert>
          ) : null}
          {mutation.isError ? (
            <Alert severity="error">
              {extractApiErrorMessage(mutation.error, t('profile.saveError'))}
            </Alert>
          ) : null}
          <TextField
            label={t('profile.username')}
            value={currentUser?.username ?? ''}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <TextField
            label={t('profile.email')}
            value={currentUser?.email ?? ''}
            fullWidth
            InputProps={{ readOnly: true }}
          />
          <Controller
            name="displayName"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label={t('profile.displayName')}
                required
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="phone"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label={t('profile.phone')}
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="position"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label={t('profile.position')}
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                value={field.value ?? ''}
                label={t('profile.description')}
                multiline
                rows={3}
                fullWidth
                error={Boolean(fieldState.error)}
                helperText={fieldState.error?.message}
              />
            )}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="submit" variant="contained" disabled={mutation.isPending || !formState.isValid}>
              {t('common.save')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
}
